
import { Vue, Component, Prop, Watch} from "vue-property-decorator";
import moment from "moment";
import { cloneDeep } from "lodash";

import GET_CONTRACTORS from "@/apollo/scheduler/GetContractors.gql";
import FormGroup from "@/components/form/FormGroup.vue";
import Scheduler from "@/components/scheduler/Scheduler.vue";
import ContractorViewActivityItem from "@/components/scheduler/itemComponents/ContractorViewActivityItem.vue";
import EmployeeGroup from "@/components/scheduler/groupComponents/EmployeeGroup.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModal.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ContractorViewAdditionalFilters from "@/components/scheduler/additionalFilters/ContractorViewAdditionalFilters.vue";
import UiButton from "@/components/button/ui-button.vue";
import SchedulerCustomModalHeader from "@/components/scheduler/SchedulerCustomModalHeader.vue";
import ContractorFilter from "../../components/scheduler/additionalFilters/ContractorFilter.vue";

import { defaultErrorNotificationMessage } from "@/data/errorMessages";
import Message from '@/data/notificationsMessages';
import { NOTIFICATION } from "@/entities/Events";
import Roles from "@/entities/Roles";
import {
  Contractor,
  Contractor_Bool_Exp,
  Project_Activity,
  Project_Activity_Bool_Exp,
  User,
  User_Profile_Bool_Exp,
} from "@/generated/graphql";
import getFullName from "@/helpers/getFullName";
import RangeChangedPayload from "@/entities/scheduler/RangeChangedPayload";
import getFetchMoreStartDates from "@/helpers/scheduler/getFetchMoreStartDates";
import TimelineOptionsWithTemplates from "@/entities/scheduler/TimelineOptionsWithTemplates";
import CustomTimelineGroup from "@/entities/scheduler/CustomTimelineGroup";
import ContractorViewActivity from "@/entities/scheduler/ContractorViewActivity";
import SelectPayload from "@/entities/scheduler/SelectPayload";
import RouteNames from "@/entities/RouteNames";
import RouteParams from "@/entities/RouteParams";
import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import NewlyCreatedSchedulerItem from "@/entities/scheduler/NewlyCreatedSchedulerItem";
import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";
import ContractorViewFiltersToWatch from "@/entities/scheduler/additionalFiltersInterfaces/ContractorViewFiltersToWatch";
import { getEmptyFiltersResultMessage, getEmptyListMessage } from "@/helpers/lists/emptyListMessages";
import contractorViewFiltersTemplate from "@/data/filtersTemplates/scheduler/contractorViewFiltersTemplate";

@Component({
  components: {
    FormGroup,
    ContractorViewActivityItem,
    EmployeeGroup,
    Scheduler,
    LoadingOverlay,
    ContractorViewAdditionalFilters,
    ActivityFormModal,
    UiButton,
    SchedulerCustomModalHeader,
    ContractorFilter
  },
  apollo: {
    contractors: {
      query: GET_CONTRACTORS,
      update (data) {
        return data.contractor
      },
      skip: true,
    }
  },
})
export default class ContractorScheduler extends Vue {
  @Prop({type: Object, required: true}) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Date, default: undefined }) readonly axisStart?: Date;
  @Prop({ type: Date, default: undefined }) readonly axisEnd?: Date;
  @Prop({ type: Array, default: () => [] }) readonly selectedContractorsIds?: string[];
  @Prop({ type: Boolean, default: false }) readonly showAdditionalFilters!: boolean;
  @Prop({ type: Boolean, default: false }) readonly shouldClearFilters!: boolean;

  contractors: Contractor[] = [];
  additionalFilters: ContractorViewFiltersToWatch = cloneDeep(contractorViewFiltersTemplate);
  queryStartTime: Date = moment().startOf('month').subtract(2, 'w').toDate();
  queryEndTime: Date = moment().endOf('month').toDate();
  numberOfAppliedFilters: number = 0;
  passedStartDate: Date = moment().startOf('month').toDate();
  passedEndDate: Date = moment().endOf('month').toDate();


  modalShown: boolean = false;
  activityInfoForModal?: ActivityDataForModal | null = null;
  projectInfoForModal?: ProjectDataForModal | null = null;
  contractorIdForModal?: string = "";
  newlyCreatedItemId?: string = "";
  isEdittingLocked: boolean = true;

  created() {
    this.$apollo.queries.contractors.setVariables({
      where: this.where,
      activitiesWhere: this.activitiesWhere,
    });
    this.$apollo.queries.contractors.skip = false;
  }

  get linkToContractorProjects(): RouteParams<{ id: string }> {
    const contractorId = this.contractorIdForModal || this.activityInfoForModal?.contractor_id;
    return{
      name: RouteNames.CONTRACTOR_DETAILS_PROJECTS,
      params: { id: contractorId || ""}
    }
  }

  @Watch("propToWatch")
  refetchOnDataFiltersChange(oldVal: string[], newVal: string[]) {
    if (oldVal !== newVal) {
      this.$apollo.queries.contractors.refetch({
        where: this.where,
        activitiesWhere: this.activitiesWhere,
      });
    }
  }

  @Watch("numberOfAppliedFilters", { immediate: true })
  appliedFiltersChanged() {
    this.$emit("appliedFiltersChanged", this.numberOfAppliedFilters);
  }

  get loading(): boolean {
    return this.$apollo.queries.contractors.loading && !this.contractors.length
  }

  get shouldBlockEditting(): boolean {
    return this.isEdittingLocked && !Boolean(this.activityInfoForModal?.isNew);
  }

  get customPlaceholderMessage(): string {
    if (this.numberOfAppliedFilters && !this.contractors.length) {
      return getEmptyFiltersResultMessage("contractors");
    }
    return getEmptyListMessage("contractors");
  }


  get propToWatch(): string {
    return JSON.stringify([
      this.selectedContractorsIds,
      this.additionalFilters,
    ])
  }

  get options(): TimelineOptionsWithTemplates {
    return {
      start: this.passedStartDate,
      end: this.passedEndDate,
      autoResize: true,
      onAdd: function(item, callback) {
        item.className = "activityColor newItem";
        callback(item);
      },
    }
  }

  get showLinkToProject(): boolean {
    return Boolean(!this.activityInfoForModal?.isNew);
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get contractorGroups(): CustomTimelineGroup[] {
    if (!this.contractors) {
      return [];
    }
    return this.contractors.map(contractor => {
      const contractorUser = contractor.user;
      return {
        id: contractor.id,
        name: getFullName(contractorUser.given_name!, contractorUser.family_name!),
        avatar: contractorUser.avatar || "",
      }
    });
  }

  get activitiesIds(): string[] {
    return this.contractors
      .map(contractor => {
        return contractor.project_activities.map(activity => activity.id)
      })
      .flat();
  }

  get contractorActivityItems(): ContractorViewActivity[] {
    if (!this.contractors) {
      return [];
    }
    const activities = this.contractors.map(contractor => {
      const groupId = contractor.id;

      return contractor.project_activities.map(contractorActivity => {
        const projectStartDate = contractorActivity.project.start_date;
        const activityStart = Boolean(contractorActivity?.start_date)
          ? contractorActivity?.start_date
          : projectStartDate;
        let activityEnd =  Boolean(contractorActivity?.end_date )
          ? contractorActivity?.end_date
          : moment(projectStartDate).add(1, 'd').toDate();
        if (activityStart === activityEnd) {
          activityEnd = moment(contractorActivity.start_date).add(24,'hours').toDate();
        }
        return {
          id: contractorActivity.id,
          group: groupId,
          content: contractorActivity.title,
          className: "activityColor",
          start: activityStart,
          end: activityEnd,
          entityInfo: {
            activity: contractorActivity as ActivityDataForModal,
            project: contractorActivity.project as ProjectDataForModal,
            contractorId: contractor.id,
          },
        };
      })
    })

    return activities.flat();
  }

  get activitiesWhere(): Project_Activity_Bool_Exp {
    return {
      _or: [
        {
          start_date: {
            _lte: this.queryEndTime,
            _gt: this.queryStartTime,
          }
        },
        {
          end_date: {
            _gte: this.queryStartTime,
            _lte: this.queryEndTime,
          }
        }
      ]
    }
  }

  get where(): Contractor_Bool_Exp {
    const {
      countries,
      states,
      cities,
      titles,
      skills,
      minRate,
      maxRate,
    } = this.additionalFilters;

    const filterConditions: Contractor_Bool_Exp[] = [{
      _and: [
        { approved: { _eq: true } },
        { deleted: { _eq: false } },
      ]
    }];

    if (this.isManager) {
      filterConditions.push({
        managers: {
          approved: {
            _eq: true,
          },
          manager: {
            user_id: {
              _eq: this.currentUser.id,
            },
          }
        }
      })
    }

    if (this.selectedContractorsIds?.length) {
      filterConditions.push({
        id: {
          _in: this.selectedContractorsIds
        }
      })
    }

    if (countries.length) {
      filterConditions.push({
        user: {
          profile: {
            country: { _in: countries },
          },
        },
      });
    }

    if (states.length) {
      filterConditions.push({
        user: {
          profile: {
            state: { _in: states},
          },
        },
      });
    }

    if (cities.length) {
      filterConditions.push({
        user: {
          profile: {
            city: { _in: cities },
          },
        },
      });
    }

    if (titles.length) {
      filterConditions.push({
        user: {
          profile: {
            job_title: { _in: titles },
          },
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        user: {
          profile: {
            skills: {
              skill_id: { _in: skills },
            },
          },
        },
      });
    }

    if (minRate || maxRate) {
      const minConditions: User_Profile_Bool_Exp[] = [];
      const maxConditions: User_Profile_Bool_Exp[] = [];

      if (minRate) {
        minConditions.push({ min_rate: { _gte: minRate } });
        maxConditions.push({ max_rate: { _gte: minRate } });
      }
      if (maxRate) {
        minConditions.push({ min_rate: { _lte: maxRate } });
        maxConditions.push({ max_rate: { _lte: maxRate } });
      }

      if (minConditions.length && maxConditions.length) {
        filterConditions.push({
          user: {
            profile: {
              _or: [{ _and: minConditions }, { _and: maxConditions }],
            },
          },
        });
      }
    }

    return { _and: filterConditions };
  }

  getOverlappingActivities(item: ContractorViewActivity): string[] {
    if (!item.entityInfo) {
      return [];
    }
    const contractorId = item.entityInfo.contractorId || "";
    const contractorToCheck: Contractor = this.contractors.filter(contractor => contractor.id === contractorId)[0];
    const contractorActivities: Project_Activity[] = contractorToCheck?.project_activities || [];

    const overlappingActivities = contractorActivities.filter(activity => {
      return contractorActivities.some(activityOne => {
        if (activityOne.id == activity.id) {
          return false;
        }
        if (activityOne.start_date >= activity.start_date && activityOne.start_date <= activity.end_date){
          return true;
        }
        if (activity.start_date >= activityOne.start_date && activity.start_date <= activityOne.end_date) {
          return true;
        }
        return false;
      });
    });
    return overlappingActivities.map(activity => activity.id);
  }

  async rangeChanged(e: RangeChangedPayload): Promise<void> {
    this.$emit('rangechanged', e);
    const fetchMoreDates = getFetchMoreStartDates(this.queryStartTime, this.queryEndTime, e);
    if (!fetchMoreDates) {
      return;
    }
    const { queryStartTime, queryEndTime, whereDatesConditions } = fetchMoreDates;
    this.queryStartTime = queryStartTime;
    this.queryEndTime = queryEndTime;

    try {
      await this.$apollo.queries.contractors.fetchMore({
        variables: {
          where: this.where,
          activitiesWhere: { _or: whereDatesConditions },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {

          const updatedContractors: Contractor[] = fetchMoreResult.contractor;
          const res = {
            contractor: previousResult.contractor.map((contractor: Contractor) => {
              const correspondingContractor: Contractor | undefined = updatedContractors.find(updatedContractor => {
                return updatedContractor.id === contractor.id;
              });
              if (correspondingContractor) {
                const newActivities = correspondingContractor.project_activities.filter(activity =>{
                  return !this.activitiesIds.includes(activity.id);
                });
                return {
                  ...contractor,
                  project_activities: Array.from(new Set([...contractor.project_activities, ...newActivities])),
                  __typename: "contractor"
                }
              } else {
                return contractor;
              }
            })
          }
          return res;
        }
      })
    } catch(e) {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage(defaultErrorNotificationMessage),
        );
      }
    };

    handleSelect(payload: SelectPayload): void {
      const selectedItemId: string = payload?.items?.[0];
      const selectedItem: ContractorViewActivity = this.contractorActivityItems.filter(activity => activity.id === selectedItemId)[0];
      if (!selectedItem) {
        this.newlyCreatedItemId = selectedItemId;
        return;
      }
      this.clearContractorIdForModal();
      this.activityInfoForModal = selectedItem.entityInfo!.activity! as ActivityDataForModal;
      this.projectInfoForModal =  selectedItem.entityInfo!.project! as ProjectDataForModal;

      this.modalShown = true;
    }

    handleCloseModal(): void {
      if (this.modalShown == false) {
        return;
      }
      this.clearSelectedItems();
      if (this.newlyCreatedItemId) {
        this.clearNewlyCreatedItemId();
        this.setCurrentlyFetchedItems();
      }
      this.clearContractorIdForModal();
      this.closeModal();
    }

    async handleCollectionUpdated(): Promise<void> {
      this.clearNewlyCreatedItemId();
      await this.refetchCollection();
      this.closeModal();
    }

    async refetchCollection(): Promise<void> {
      await this.$apollo.queries.contractors.refetch({
        where: this.where,
        activitiesWhere: this.activitiesWhere,
      });
    }

    clearSelectedItems(): void {
      const timeline = this.$refs.timeline as any;
      timeline.setSelection([]);
    }

    clearNewlyCreatedItemId(): void {
      this.newlyCreatedItemId = undefined;
    }

    clearContractorIdForModal(): void {
      this.contractorIdForModal = undefined;
    }

    closeModal(): void {
      this.modalShown = false;
      this.isEdittingLocked = true;
    }

    setCurrentlyFetchedItems(): void {
      const timeline = this.$refs.timeline as any;
      timeline.setItems(this.contractorActivityItems);
    }

    handleIconClose(): void {
      this.$emit('close')
    }
    handleCreateBtnClicked(activity: NewlyCreatedSchedulerItem) {
      this.activityInfoForModal = {
        title: "",
        contractor_id: activity.group,
        end_date: "",
        note: "",
        budget: "",
        confirmed_hours: 0,
        requested_hours: 0,
        isNew: true,
      };
      this.setCurrentlyFetchedItems();
      this.projectInfoForModal = undefined;
      this.contractorIdForModal = activity.group;
      this.modalShown = true;
    }

    handleFiltersChanged(filters: ContractorViewFiltersToWatch) {
      this.additionalFilters.countries = filters.countries;
      this.additionalFilters.states = filters.states;
      this.additionalFilters.cities = filters.cities;
      this.additionalFilters.titles = filters.titles;
      this.additionalFilters.skills = filters.skills;
      this.additionalFilters.minRate = filters.minRate;
      this.additionalFilters.maxRate = filters.maxRate;
    }

    handleAppliedFiltersNumberChanged(num: number) {
      this.numberOfAppliedFilters = num;
    }

    handleTimeRangeChanged(rangeStart: Date) : void {
      this.passedStartDate = rangeStart
    }

    handleTimeRangeChangedEnd(rangeEnd: Date): void {
      this.passedEndDate = rangeEnd
    }

    handleEditClick(): void {
      this.isEdittingLocked = false;
    }
  }
